import React, { useState, useMemo, useRef } from 'react';
import selectionStorage from '../utils/selectionStorage';
import {
  X, Plus, Trash2, AlertCircle, CheckCircle, ChevronUp, ChevronDown,
  Play, RefreshCw, Copy, Search, Code, Bug, ExternalLink
} from 'lucide-react';
import syncService from '../services/syncService';
import { config } from '../config';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';

// Postman-style OHIP request replay (superadmin). Seeded from a captured exchange; the request is sent
// through the backend (oracle-ohip) which injects the task's target-connector auth. The user can edit
// method/url/headers/body and re-send, then inspect the live response — for debugging & fixing OHIP calls.
const SIM_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'];
// `sim` (optional) makes this a shared component: pass { simulate(request), loadToken(), curlPath, jira }
// to route the send/token/curl through a different backend (e.g. Payment Porter's ohip-simulate). When
// omitted it falls back to the sync task path (syncService + syncId/taskId) — MessageLogPage's usage.
function OhipSimulateModal({ seed, syncId, taskId, onClose, sim }) {
  const doSimulate  = sim?.simulate  || ((request) => syncService.ohipSimulate(syncId, taskId, request));
  const doLoadToken = sim?.loadToken || (() => syncService.ohipSimulate(syncId, taskId, { tokenOnly: true }));
  const curlPath    = sim?.curlPath  || `/sync/${syncId}/task/${taskId}/ohip-simulate`;
  const jiraEnabled = sim ? !!sim.jira : true;
  const seedBody = seed.body == null ? '' : (typeof seed.body === 'string' ? seed.body : (() => { try { return JSON.stringify(seed.body, null, 2); } catch (e) { return String(seed.body); } })());
  // Prefill from the captured request headers (excluding Authorization — the server injects a fresh
  // token). So Postman replays exactly the headers oracle-ohip sent, editable before Send.
  const seedHeaderRows = (seed.headers && typeof seed.headers === 'object')
    ? Object.entries(seed.headers).filter(([k]) => !/^authorization$/i.test(k)).map(([key, value]) => ({ key, value: String(value) }))
    : [];
  const [method, setMethod] = useState((seed.method || 'GET').toUpperCase());
  const [url, setUrl] = useState(seed.url || '');
  const [headers, setHeaders] = useState(seedHeaderRows.length ? seedHeaderRows : [{ key: '', value: '' }]);
  const [body, setBody] = useState(seedBody);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [curlCopied, setCurlCopied] = useState(false);
  const [jsonStatus, setJsonStatus] = useState(null); // { ok, message } after Validate JSON, or null
  // Jira-from-simulation: the requester confirms the simulated message is what the pipeline SHOULD
  // send, and the ticket carries the sim request/response so the fix can reproduce it. Triggered from
  // the footer Jira button (enabled after a successful Send) via a confirm dialog.
  const [simJiraModalOpen, setSimJiraModalOpen] = useState(false);
  const [simConfirmed, setSimConfirmed] = useState(false);
  const [simJiraNote, setSimJiraNote] = useState('');
  const [simJiraCreating, setSimJiraCreating] = useState(false);
  const [simJiraTicket, setSimJiraTicket] = useState(null); // { key, url }
  const [simJiraError, setSimJiraError] = useState(null);

  const openSimJira = () => {
    setSimConfirmed(false);
    setSimJiraNote('');
    setSimJiraError(null);
    setSimJiraModalOpen(true);
  };

  const createSimJira = async () => {
    const uuid = seed.record?.sprRecordUuid || seed.record?.recordUuid;
    if (!uuid || simJiraCreating) return;
    // NOT a gate. A simulation is worth a ticket in BOTH directions: "this body is the fix" and
    // "I have reproduced it and the body is NOT the fix — the cause is elsewhere". The second is the
    // case the cutover bug produced (arrivalDate forced to the cutover date, so no edit to the body
    // could fix it), and it was impossible to raise. What the ticket must never do is CLAIM the body
    // is validated when it isn't, so the flag travels with it and the heading changes to match.
    //
    // Unconfirmed means the note is the only thing that explains the real cause, so it is required.
    if (!simConfirmed && !simJiraNote.trim()) {
      setSimJiraError('Say what is actually wrong — without the tick, the note is the only thing telling the fix where to look.');
      return;
    }
    setSimJiraError(null);
    try {
      setSimJiraCreating(true);
      const hdrs = {};
      headers.forEach(h => { if (h.key && h.key.trim() && !/^authorization$/i.test(h.key.trim())) hdrs[h.key.trim()] = h.value; });
      let bodyVal = body && body.trim() ? body : undefined;
      if (bodyVal) { try { bodyVal = JSON.parse(bodyVal); } catch (e) { /* raw string */ } }
      const res = await syncService.createJiraTicket(syncId, taskId, {
        recordUuid: uuid,
        pageUrl: window.location.href,
        note: simJiraNote.trim() || undefined,
        simulation: {
          confirmedCorrect: simConfirmed,
          method, url, headers: hdrs,
          request: bodyVal,
          responseStatus: result?.status,
          response: result?.response,
        },
      });
      setSimJiraTicket({ key: res.key, url: res.url });
      setSimJiraModalOpen(false);
    } catch (err) {
      setSimJiraError(err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Failed to create Jira ticket');
    } finally {
      setSimJiraCreating(false);
    }
  };

  // Live curl for the request the modal makes to OUR backend (the ohip-simulate proxy) — NOT the raw
  // OHIP call. Mirrors send(): POST {apiBaseUrl}/sync/{syncId}/task/{taskId}/ohip-simulate with the
  // context query param the axios interceptor appends for /sync/{uuid} routes, and a body of
  // { method, url, headers, body }. The Authorization header carries the real idToken (same value the
  // interceptor injects — no "Bearer " prefix) so the curl is runnable as-is.
  const curlCommand = useMemo(() => {
    const base = (config.apiBaseUrl || '').replace(/\/$/, '');
    const idToken = localStorage.getItem('idToken') || '$ID_TOKEN';
    const { hotelId, accountId, partnerId } = selectionStorage.getContext();
    // The /sync/{uuid} axios interceptor appends the selected context as a query param; other backends
    // (e.g. Payment Porter) don't, so only add it on the sync fallback path.
    const qs = sim ? '' : (hotelId ? `?hotelId=${hotelId}` : accountId ? `?accountId=${accountId}` : partnerId ? `?partnerId=${partnerId}` : '');
    const hdrs = {};
    headers.forEach(h => { if (h.key && h.key.trim()) hdrs[h.key.trim()] = h.value; });
    let bodyVal = body && body.trim() ? body : undefined;
    if (bodyVal) { try { bodyVal = JSON.parse(bodyVal); } catch (e) { /* send raw string */ } }
    const payload = { method, url, headers: hdrs, ...(bodyVal !== undefined ? { body: bodyVal } : {}) };
    const json = JSON.stringify(payload, null, 2).replace(/'/g, `'\\''`);
    const shellSafe = (v) => String(v).replace(/'/g, `'\\''`);
    return [
      `curl -X POST \\`,
      `  '${base}${curlPath}${qs}' \\`,
      `  -H 'Authorization: ${shellSafe(idToken)}' \\`,
      `  -H 'Content-Type: application/json' \\`,
      `  -d '${json}'`,
    ].join('\n');
  }, [method, url, headers, body, curlPath, sim]);

  const copyCurl = () => {
    navigator.clipboard.writeText(curlCommand).then(() => { setCurlCopied(true); setTimeout(() => setCurlCopied(false), 1500); }).catch(() => {});
  };
  const [bodyCopied, setBodyCopied] = useState(false);
  const copyBody = () => {
    navigator.clipboard.writeText(body || '').then(() => { setBodyCopied(true); setTimeout(() => setBodyCopied(false), 1500); }).catch(() => {});
  };
  const bodyRef = useRef(null);
  const [bodySearch, setBodySearch] = useState('');
  const [matchIdx, setMatchIdx] = useState(-1); // ordinal of the currently-highlighted match, -1 = none yet

  // Start offsets of every match, in order.
  const bodyMatches = useMemo(() => {
    if (!bodySearch) return [];
    const hay = body.toLowerCase();
    const needle = bodySearch.toLowerCase();
    const out = [];
    let i = hay.indexOf(needle);
    while (i !== -1) { out.push(i); i = hay.indexOf(needle, i + needle.length); }
    return out;
  }, [body, bodySearch]);

  const scrollToMatch = (idx) => {
    const ta = bodyRef.current;
    if (!ta || idx == null || idx < 0) return;
    // Scroll the match into view. \n-counting fails for a minified (single-line) body that only
    // soft-wraps, so measure the actual rendered height of the text up to the match by briefly
    // truncating the textarea's value and reading scrollHeight, then restore it.
    const fullValue = ta.value;
    ta.value = body.slice(0, idx);
    const uptoHeight = ta.scrollHeight;
    ta.value = fullValue;
    ta.focus();
    ta.setSelectionRange(idx, idx + bodySearch.length);
    ta.scrollTop = Math.max(0, uptoHeight - ta.clientHeight / 2);
  };

  const gotoMatch = (ord) => {
    const n = bodyMatches.length;
    if (n === 0) return;
    const wrapped = ((ord % n) + n) % n; // wrap around both ends
    setMatchIdx(wrapped);
    scrollToMatch(bodyMatches[wrapped]);
  };
  const nextMatch = () => gotoMatch(matchIdx + 1);          // first press → match 1
  const prevMatch = () => gotoMatch(matchIdx <= 0 ? bodyMatches.length - 1 : matchIdx - 1);

  const validateJson = () => {
    if (!body || !body.trim()) { setJsonStatus({ ok: false, message: 'Body is empty' }); return; }
    try {
      JSON.parse(body);
      setJsonStatus({ ok: true, message: 'Valid JSON' });
    } catch (e) {
      setJsonStatus({ ok: false, message: e.message });
    }
  };

  const prettyPrintJson = () => {
    if (!body || !body.trim()) { setJsonStatus({ ok: false, message: 'Body is empty' }); return; }
    try {
      setBody(JSON.stringify(JSON.parse(body), null, 2));
      setJsonStatus({ ok: true, message: 'Formatted' });
    } catch (e) {
      setJsonStatus({ ok: false, message: e.message });
    }
  };

  const getToken = async () => {
    setTokenLoading(true); setToken(null); setError(null);
    try {
      const res = await doLoadToken();
      if (res && res.token) setToken(res);
      else setError((res && res.error) || 'Failed to get token');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to get token');
    } finally {
      setTokenLoading(false);
    }
  };

  const copyToken = () => {
    if (!token) return;
    navigator.clipboard.writeText(token.token).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };

  const useTokenAsHeader = () => {
    if (!token) return;
    setHeaders(hs => {
      const others = hs.filter(h => !/^authorization$/i.test((h.key || '').trim()) && (h.key || h.value));
      return [{ key: 'Authorization', value: 'Bearer ' + token.token }, ...others];
    });
  };

  const setHeader = (i, field) => (e) => setHeaders(hs => hs.map((h, idx) => idx === i ? { ...h, [field]: e.target.value } : h));
  const addHeader = () => setHeaders(hs => [...hs, { key: '', value: '' }]);
  const removeHeader = (i) => setHeaders(hs => hs.filter((_, idx) => idx !== i));

  const pretty = (v) => {
    if (v == null || v === '') return '(empty)';
    if (typeof v === 'string') { try { return JSON.stringify(JSON.parse(v), null, 2); } catch (e) { return v; } }
    try { return JSON.stringify(v, null, 2); } catch (e) { return String(v); }
  };

  const send = async () => {
    setSending(true); setError(null); setResult(null);
    try {
      const hdrs = {};
      headers.forEach(h => { if (h.key && h.key.trim()) hdrs[h.key.trim()] = h.value; });
      let bodyVal = body && body.trim() ? body : undefined;
      if (bodyVal) { try { bodyVal = JSON.parse(bodyVal); } catch (e) { /* send raw string */ } }
      // The record uuid makes the run auditable: the server appends a [sim] entry to the record's
      // status history carrying this exact body, so what an operator sent to OHIP survives the browser
      // tab. Absent (the shared-component `sim` callers with no record) it simply isn't recorded.
      const simRecordUuid = seed.record?.sprRecordUuid || seed.record?.recordUuid;
      const res = await doSimulate({
        method, url, headers: hdrs, ...(bodyVal !== undefined ? { body: bodyVal } : {}),
        ...(simRecordUuid ? { recordUuid: simRecordUuid } : {}),
      });
      if (res && res.error && res.ok === false) setError(res.error);
      setResult(res);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Simulate failed');
    } finally {
      setSending(false);
    }
  };

  const statusClass = result && typeof result.status === 'number'
    ? (result.status < 400 ? 'story-step-success' : 'story-step-error') : '';

  return (
    <div className="modal-overlay ohip-sim-overlay" onClick={onClose}>
      <div className="modal-content ohip-sim-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Simulate OHIP request</h2>
          <button className="modal-close" onClick={onClose}><X className="icon-sm" /></button>
        </div>
        <div className="modal-body">
          <p className="form-hint" style={{ marginTop: 0 }}>
            {sim?.description || <>Sent through the task’s <strong>target connector</strong>. Headers below are prefilled from the original request (the bearer token is re-injected server-side); edit or remove any before sending.</>}
          </p>

          <div className="ohip-sim-reqline">
            <select className="form-input ohip-sim-method" value={method} onChange={(e) => setMethod(e.target.value)}>
              {SIM_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <input className="form-input ohip-sim-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/crm/v1/profiles/123  (or a /path)" />
            <Button onClick={send} disabled={sending || !url.trim()}>
              {sending ? (<><Spinner size="sm" className="icon-sm" />Sending…</>) : (<><Play size={14} /> Send</>)}
            </Button>
            {result && typeof result.status === 'number' && !sending && (
              <span className={`ohip-sim-footer-status ${result.status < 400 ? 'ohip-sim-footer-ok' : 'ohip-sim-footer-err'}`}>
                Response: <strong>{result.status}</strong>
                {typeof result.durationMs === 'number' && <> · {result.durationMs} ms</>}
              </span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Header overrides</label>
            {headers.map((h, i) => (
              <div key={i} className="ohip-sim-header-row">
                <input className="form-input" placeholder="Header" value={h.key} onChange={setHeader(i, 'key')} />
                <input className="form-input" placeholder="Value" value={h.value} onChange={setHeader(i, 'value')} />
                <button type="button" className="icon-button" title="Remove" onClick={() => removeHeader(i)}><Trash2 size={14} /></button>
              </div>
            ))}
            <div className="ohip-sim-header-actions">
              <button type="button" className="ohip-sim-addheader" onClick={addHeader}><Plus size={13} /> Add header</button>
              <button type="button" className="ohip-sim-addheader" onClick={getToken} disabled={tokenLoading}>
                {tokenLoading ? <Spinner size="sm" className="icon-sm" /> : <RefreshCw size={13} />} Get bearer token
              </button>
            </div>
            {token && token.token && (
              <div className="ohip-sim-token-box">
                <div className="ohip-sim-token-val">Bearer {token.token}</div>
                <div className="ohip-sim-token-actions">
                  <button type="button" className="message-log-exchange-back" onClick={copyToken}>{copied ? 'Copied!' : 'Copy'}</button>
                  <button type="button" className="message-log-exchange-back" onClick={useTokenAsHeader}>Use as Authorization header</button>
                  {typeof token.expiresIn === 'number' && <span className="form-hint">expires in {token.expiresIn}s · {token.gatewayUrl}</span>}
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label ohip-sim-body-label">
              <span>Body {(method === 'GET' || method === 'HEAD') && <span className="form-hint">(ignored for {method})</span>}</span>
              <span className="ohip-sim-body-actions">
                <button type="button" className="ohip-sim-addheader" onClick={validateJson}><CheckCircle size={13} /> Validate JSON</button>
                <button type="button" className="ohip-sim-addheader" onClick={prettyPrintJson}><Code size={13} /> Pretty print</button>
                <button type="button" className="ohip-sim-addheader" onClick={copyBody} disabled={!body || !body.trim()}><Copy size={13} /> {bodyCopied ? 'Copied' : 'Copy'}</button>
              </span>
            </label>
            <div className="ohip-sim-body-search">
              <Search size={14} className="ohip-sim-body-search-icon" />
              <input
                className="form-input"
                placeholder="Search body…"
                value={bodySearch}
                onChange={(e) => { setBodySearch(e.target.value); setMatchIdx(-1); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.shiftKey ? prevMatch() : nextMatch(); } }}
              />
              {bodySearch && (
                <span className="form-hint ohip-sim-body-search-count">
                  {bodyMatches.length === 0 ? 'No matches' : `${matchIdx >= 0 ? matchIdx + 1 : 1} of ${bodyMatches.length}`}
                </span>
              )}
              <button type="button" className="icon-button" title="Previous match (Shift+Enter)" onClick={prevMatch} disabled={bodyMatches.length === 0}><ChevronUp size={16} /></button>
              <button type="button" className="icon-button" title="Next match (Enter)" onClick={nextMatch} disabled={bodyMatches.length === 0}><ChevronDown size={16} /></button>
            </div>
            <textarea ref={bodyRef} className="form-input ohip-sim-body" rows={28} value={body} onChange={(e) => { setBody(e.target.value); if (jsonStatus) setJsonStatus(null); }} placeholder="Request body (JSON)" />
            {jsonStatus && (
              <div className={`ohip-sim-json-status ${jsonStatus.ok ? 'ohip-sim-json-ok' : 'ohip-sim-json-err'}`}>
                {jsonStatus.ok ? <CheckCircle className="icon-sm" /> : <AlertCircle className="icon-sm" />}
                <span>{jsonStatus.message}</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label ohip-sim-body-label">
              <span>Backend request (curl)</span>
              <span className="ohip-sim-body-actions">
                <button type="button" className="ohip-sim-addheader" onClick={copyCurl}>
                  <Copy size={13} /> {curlCopied ? 'Copied!' : 'Copy'}
                </button>
              </span>
            </label>
            <p className="form-hint" style={{ marginTop: 0 }}>
              The request this screen sends to the <strong>ohip-simulate</strong> backend (not the raw OHIP call), including your current session token — runnable as-is.
            </p>
            <pre className="story-exchange-pre ohip-sim-curl-pre">{curlCommand}</pre>
          </div>

          {error && <div className="error-banner"><AlertCircle className="icon-sm" />{error}</div>}

          {result && !result.error && (
            <div className="ohip-sim-response">
              <div className={`ohip-sim-status ${statusClass}`}>
                Response: <strong>{result.status}</strong>
                {typeof result.durationMs === 'number' && <span className="ohip-sim-duration"> · {result.durationMs} ms</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Response body</label>
                <pre className="story-exchange-pre ohip-sim-resp-pre">{pretty(result.response)}</pre>
              </div>
              {result.responseHeaders && (
                <details className="ohip-sim-details">
                  <summary>Response headers</summary>
                  <pre className="story-exchange-pre">{pretty(result.responseHeaders)}</pre>
                </details>
              )}
              {result.request && (
                <details className="ohip-sim-details">
                  <summary>Sent request (auth redacted)</summary>
                  <pre className="story-exchange-pre">{pretty(result.request)}</pre>
                </details>
              )}

            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose} type="button">Close</Button>
          {jiraEnabled && seed.record && (
            simJiraTicket ? (
              <a className="btn btn-secondary sa-only mlp-jira-link" href={simJiraTicket.url} target="_blank" rel="noreferrer" title="Open the Jira ticket created from this simulation">
                <ExternalLink className="icon-sm" /> {simJiraTicket.key}
              </a>
            ) : (
              <Button
                variant="secondary"
                className="sa-only"
                onClick={openSimJira}
                disabled={simJiraCreating}
                title="Send this corrected message to Jira so the fix can reproduce it (the OHIP response is attached if you have sent it)"
              >
                <Bug className="icon-sm" /> {simJiraCreating ? 'Creating…' : 'Jira'}
              </Button>
            )
          )}
          {result && typeof result.status === 'number' && !sending && (
            <span className={`ohip-sim-footer-status ${result.status < 400 ? 'ohip-sim-footer-ok' : 'ohip-sim-footer-err'}`}>
              Response: <strong>{result.status}</strong>
              {typeof result.durationMs === 'number' && <> · {result.durationMs} ms</>}
            </span>
          )}
          <Button onClick={send} disabled={sending || !url.trim()}>
            {sending ? (<><Spinner size="sm" className="icon-sm" />Sending…</>) : 'Send'}
          </Button>
        </div>

        {simJiraModalOpen && (
          <ConfirmModal
            title="Create Jira ticket from this simulation"
            message={`The ticket carries this exact request/response (plus ${seed.record?.sourcerecordid ? `record ${seed.record.sourcerecordid}` : 'the record'}'s message-log context) so the fix can reproduce it.`}
            confirmLabel={simJiraCreating ? 'Creating…' : 'Create ticket'}
            onConfirm={createSimJira}
            onCancel={() => setSimJiraModalOpen(false)}
          >
            <label className="ohip-sim-jira-confirm">
              <input
                type="checkbox"
                checked={simConfirmed}
                onChange={e => { setSimConfirmed(e.target.checked); setSimJiraError(null); }}
              />
              <span>This simulated message is correct — the fix should make the pipeline send this request</span>
            </label>
            <p className="ohip-sim-jira-hint">
              {simConfirmed
                ? 'The ticket will state that this exact request is the target, and the fix will be diffed against it.'
                : 'Leave it unticked when the body is NOT the fix — the request goes on the ticket as evidence only, and your note below has to say where the real cause is.'}
            </p>
            <textarea
              className="cm-textarea"
              value={simJiraNote}
              onChange={e => { setSimJiraNote(e.target.value); setSimJiraError(null); }}
              placeholder={simConfirmed
                ? 'Describe the change you want (e.g. what was edited vs the original request and why)'
                : 'Required — what is actually wrong? (e.g. the guest is mid-stay and the cutover rule moves arrivalDate onto the departure date, so no edit to this body can fix it)'}
            />
            {simJiraError && <div className="error-banner"><AlertCircle className="icon-sm" />{simJiraError}</div>}
          </ConfirmModal>
        )}
      </div>
    </div>
  );
}

export default OhipSimulateModal;
